/**
 * Keeping the app itself current (`04-launcher-and-patcher.md` section 1.2).
 *
 * Desktop only. The Tauri updater reads the signed manifest published beside the installers,
 * checks at launch and every half hour, and downloads in the background. Installing is the only
 * part anybody has to notice, so it waits for a restart they ask for.
 *
 * Downloading and installing are deliberately two steps rather than `downloadAndInstall`. On
 * Windows the install takes the running app down with it, and doing that the moment a manifest
 * changes would close a launcher mid-download of a game.
 *
 * Android updates through `PackageInstaller` against `/v1/app/android/latest` instead, and none
 * of that is wired up yet.
 */

import * as React from 'react';
import { isDesktop } from '../platform/index.js';

/**
 * Section 1.2: at launch, and every half hour after it.
 *
 * The app is meant to be left running — it stays in the tray after the window closes — so this
 * is the only thing standing between a build going out and the people who already have the app
 * open hearing about it. A manifest fetch is a few hundred bytes.
 */
export const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * How long to wait after a check that failed.
 *
 * A failed check is nearly always a network that is not there, and an app open on a machine that
 * has just come back online should not be half an hour behind for it.
 */
export const RETRY_INTERVAL_MS = 5 * 60 * 1000;

/**
 * How long "Later" on the update banner lasts.
 *
 * Deliberately not the check interval. The check is cheap and silent and wants to be often; the
 * banner is the one interruption an app update is allowed, and being offered the same restart
 * every half hour is what makes people stop reading it.
 */
export const SNOOZE_MS = 6 * 60 * 60 * 1000;

/**
 * How often the scheduler reads the clock.
 *
 * Not how often it checks: every tick compares the time against when the next check is due, and
 * almost all of them do nothing. A minute is also where a hidden window's timers get throttled
 * to anyway, so a launcher sitting in the background pays for this once a minute at most.
 */
const TICK_MS = 60 * 1000;

export interface AppUpdateState {
  status: 'idle' | 'checking' | 'downloading' | 'ready' | 'installing' | 'failed';
  /** The version waiting to be installed, once there is one. */
  version: string | null;
  /** 0 to 1 while downloading, and 1 once there is something to install. */
  progress: number;
  /** Why the last attempt gave up, for the one screen that has room to say so. */
  error: string | null;
}

const IDLE: AppUpdateState = { status: 'idle', version: null, progress: 0, error: null };

type Listener = (state: AppUpdateState) => void;
const listeners = new Set<Listener>();
let current: AppUpdateState = IDLE;

/** The downloaded update, held between the download finishing and the user restarting. */
let pending: { install: () => Promise<void>; version: string } | null = null;
/** One check at a time: the six hourly one can land while the launch one is still going. */
let inFlight: Promise<void> | null = null;
/** When the next check is due, as wall clock rather than a countdown. See `startAppUpdates`. */
let nextCheckAt = 0;

function set(patch: Partial<AppUpdateState>): void {
  current = { ...current, ...patch };
  for (const listener of listeners) listener(current);
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function getAppUpdate(): AppUpdateState {
  return current;
}

export function useAppUpdate(): AppUpdateState {
  const [state, setState] = React.useState(current);
  React.useEffect(() => {
    listeners.add(setState);
    // A check that finished before this mounted would otherwise never reach the screen.
    setState(current);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}

/**
 * Looks for a newer version and downloads it.
 *
 * Nothing here is worth interrupting anyone over. A check that fails is a check that happens
 * again shortly, and the app works exactly as well in the meantime, so the failure is recorded
 * and never raised.
 */
export function checkForUpdate(): Promise<void> {
  if (!isDesktop) return Promise.resolve();
  // Something already downloaded is the newest thing this run is going to see.
  if (current.status === 'ready' || current.status === 'installing') return Promise.resolve();
  inFlight ??= run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(): Promise<void> {
  // Booked before the work starts rather than after it, so a check that takes a long time to
  // fail does not push the next one out by however long it spent failing.
  nextCheckAt = Date.now() + CHECK_INTERVAL_MS;
  set({ status: 'checking', error: null });
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) {
      set({ status: 'idle', version: null, progress: 0 });
      return;
    }

    set({ status: 'downloading', version: update.version, progress: 0 });

    let downloaded = 0;
    let total = 0;
    await update.download((event) => {
      if (event.event === 'Started') total = event.data.contentLength ?? 0;
      else if (event.event === 'Progress') {
        downloaded += event.data.chunkLength;
        // A manifest without a content length still downloads; it just cannot be drawn.
        if (total > 0) set({ progress: Math.min(1, downloaded / total) });
      }
    });

    pending = { install: () => update.install(), version: update.version };
    set({ status: 'ready', version: update.version, progress: 1 });
    announce(update.version);
  } catch (error) {
    nextCheckAt = Date.now() + RETRY_INTERVAL_MS;
    set({ status: 'failed', error: messageOf(error) });
  }
}

/**
 * Says an update is waiting, when the banner cannot.
 *
 * With the app in the tray the window is hidden, and a banner behind a hidden window is a
 * proposal nobody is being made. This is the same offer in the only place left to make it, and
 * only then: a window somebody is looking at already has the banner in it.
 */
function announce(version: string): void {
  if (typeof document === 'undefined' || !document.hidden) return;
  void import('../notifications/useOsNotifications.js')
    .then(({ notify }) =>
      notify('GOOD BUILD has an update', `Version ${version} installs when you restart the app.`),
    )
    .catch(() => {});
}

/**
 * Installs what has been downloaded and restarts into it.
 *
 * There is nothing worth writing after this call. On Windows the installer replaces an exe that
 * is still running by taking the app down with it, so the relaunch below is what Linux needs and
 * what Windows never reaches.
 *
 * The blocking gate can ask for this before the background download has finished, so a run with
 * nothing pending does the download first rather than refusing.
 */
export async function installUpdate(): Promise<void> {
  if (!isDesktop || current.status === 'installing') return;
  if (!pending) {
    await checkForUpdate();
    if (!pending) return;
  }

  set({ status: 'installing' });
  try {
    await pending.install();
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  } catch (error) {
    set({ status: 'failed', error: messageOf(error) });
  }
}

/**
 * Starts the check at launch and keeps it running for as long as the app is open.
 *
 * This app is meant to be left open for days — it keeps running in the tray after the window
 * closes — which is the case a plain `setInterval` is worst at. A machine that suspends does not
 * fire timers while it is asleep, so an app that was open overnight would come back and then
 * wait out the rest of an interval that wall clock time has already spent. So the timer here
 * only reads the clock, and whether to check is decided against when the next check was due.
 *
 * Returns the way to stop it.
 */
export function startAppUpdates(): () => void {
  if (!isDesktop) return () => {};

  void checkForUpdate();

  const tick = (): void => {
    if (Date.now() >= nextCheckAt) void checkForUpdate();
  };
  const timer = setInterval(tick, TICK_MS);

  // The moments worth reading the clock off-schedule. Someone coming back to the window is the
  // only time a proposal can be acted on at all, and a network that just came back is the reason
  // the last check failed. All three go through the same due test, so a window being clicked in
  // and out of costs nothing.
  const onVisibility = (): void => {
    if (!document.hidden) tick();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', tick);
  window.addEventListener('online', tick);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', tick);
    window.removeEventListener('online', tick);
  };
}
